import React, { useEffect, useState } from "react";
import TopBar from "../components/TopBar";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { useDistrict } from "../lib/district-context";
import { getDistrictSetting, setDistrictSetting } from "../lib/api";
import { TOGGLEABLE_MODULES } from "../config/modules-registry";

// Misma key que consumirá is_module_enabled() (SQL, ya creada en Fase 1) y
// useEnabledModules() (frontend, ya creado en Fase 1) — un solo JSONB por
// distrito, { [moduleKey]: boolean }.
const SETTINGS_KEY = "enabled_modules";

/**
 * Fase 2a — persistencia real, todavía SIN conectar a ningún componente de
 * nav (Layout.jsx/District.jsx/MemberNav.jsx) ni a RLS. Eso es Fase 2b/2c.
 *
 * Lee/escribe district_settings vía getDistrictSetting/setDistrictSetting —
 * el mismo mecanismo que ya usan weekly_plan (lib/weekly-plan.js) y
 * schedule_template_* (lib/day-type-schedule.js). Esas funciones (lib/api.js)
 * YA resuelven offline por su cuenta: setDistrictSetting escribe primero a
 * localCache y, si falla la red, encola el cambio en localStorage
 * (keves_dirty_settings) para reintentar en el próximo 'online' — no hace
 * falta ninguna lógica offline adicional acá, solo no bloquear la UI
 * esperando esa promesa (patrón optimista, igual que useWeeklyPlan.update()).
 *
 * Única diferencia deliberada respecto a ese patrón: acá SÍ se muestra un
 * loading explícito para la carga inicial (useWeeklyPlan asume el default
 * local al instante) porque un switch que muestra "activado" y un segundo
 * después salta a "desactivado" es confuso — mejor esperar el valor real.
 *
 * Mismo patrón de exports que SettingsLists.jsx/SettingsPlan.jsx: *Content
 * (drawer) + default (ruta propia /settings/modules).
 */
export function SettingsModulesContent() {
  const { district, isAdmin, isPastor } = useDistrict();

  // null = todavía no se resolvió el valor real desde la base (o no hay
  // configuración explícita todavía, lo cual también se trata como "todo
  // activado" una vez terminó de cargar — ver isChecked()).
  const [enabledModules, setEnabledModules] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!district?.id) {
      // district todavía no resolvió (pasa con admins: RequireDistrict los
      // deja pasar con solo userProfile.district_id, sin esperar a que el
      // objeto district termine de cargar). NO se toca loading acá — se
      // queda en true hasta que este efecto vuelva a correr con un
      // district.id real. Resolver loading=false acá era el bug: dejaba
      // enabledModules=null y isChecked() caía en "todo activado" sin haber
      // llamado getDistrictSetting.
      return;
    }
    let cancelled = false;
    setLoading(true);

    getDistrictSetting(district.id, SETTINGS_KEY, null)
      .then((value) => {
        if (!cancelled) {
          setEnabledModules(value && typeof value === "object" ? value : {});
        }
      })
      .catch(() => {
        // getDistrictSetting ya intentó localCache por su cuenta; si aun así
        // falla, se trata como "sin configuración" (todo activado).
        if (!cancelled) setEnabledModules({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [district?.id]);

  // Mismo mecanismo de gate por rol que ya usa Settings.jsx para ocultar
  // Suscripción/otras secciones a coordinadores (!isCoordinator). Aquí es
  // "admin o pastor", explícito, porque un coordinador nunca debería llegar
  // a esta pantalla ni por drawer ni por URL directa.
  if (!isAdmin && !isPastor) return null;

  // Default true: sin key explícita para ese módulo (o sin fila
  // 'enabled_modules' del todo) -> activado. Mismo default que
  // is_module_enabled() (SQL) y useEnabledModules() (Fase 1).
  const isChecked = (key) => {
    if (!enabledModules) return true;
    if (!(key in enabledModules)) return true;
    return enabledModules[key] !== false;
  };

  const toggle = (key) => {
    if (!district?.id) return;
    const current = enabledModules ?? {};
    const next = { ...current, [key]: !isChecked(key) };

    // Optimista: el switch responde de inmediato, sin esperar la escritura.
    setEnabledModules(next);

    // setDistrictSetting ya maneja su propio fallback offline (localCache +
    // cola de reintento) — no hace falta más manejo de error acá, igual que
    // useWeeklyPlan().update().
    setDistrictSetting(district.id, SETTINGS_KEY, next).catch(() => {});
  };

  return (
    <div className="px-4 py-4">
      <p className="text-[12px] text-slate-500 dark:text-slate-400 ml-1 mb-4">
        Activa o desactiva secciones del distrito.
      </p>

      <div className="card-soft overflow-hidden divide-y divide-slate-100 dark:divide-slate-700/50">
        {loading
          ? TOGGLEABLE_MODULES.map((m) => (
              <div key={m.key} className="flex items-center justify-between gap-3 px-4 py-3.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-9 rounded-full" />
              </div>
            ))
          : TOGGLEABLE_MODULES.map((m) => (
              <div key={m.key} className="flex items-center justify-between gap-3 px-4 py-3.5">
                <p className="text-[14px] font-medium text-slate-900 dark:text-slate-100">
                  {m.label}
                </p>
                <Switch
                  checked={isChecked(m.key)}
                  onCheckedChange={() => toggle(m.key)}
                  aria-label={`Activar o desactivar ${m.label}`}
                />
              </div>
            ))}
      </div>
    </div>
  );
}

export default function SettingsModules() {
  return (
    <div className="anim-in min-h-[100dvh] bg-page-bg pb-[calc(96px+env(safe-area-inset-bottom))] font-sans">
      <TopBar title="Módulos" safeFallback="/settings" />
      <SettingsModulesContent />
    </div>
  );
}
